import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Loader2, Download, Upload, Plus, Filter, AlertCircle, FileText, CheckCircle2, RefreshCw, RotateCcw, AlertTriangle, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtINR(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function LeaveBalances() {
  const { user } = useAuth();
  const isAdminOrHR = ['tenant_admin', 'hr_admin', 'payroll_admin', 'super_admin'].includes((user?.role || '').toLowerCase());

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [year, setYear] = useState(new Date().getFullYear());
  const [selDept, setSelDept] = useState('');
  const [selLoc, setSelLoc] = useState('');
  const [selLeaveType, setSelLeaveType] = useState('');
  const [searchEmp, setSearchEmp] = useState('');

  const getInitialCycle = () => {
    try {
      const p = new URLSearchParams(window.location.search);
      const c = p.get('cycle') || p.get('month');
      if (c && /^\d{4}-\d{2}$/.test(c)) return c;
    } catch { /* */ }
    return new Date().toISOString().slice(0, 7);
  };

  const [cycle, setCycle] = useState(getInitialCycle);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showProcessorEntry, setShowProcessorEntry] = useState(false);
  const [computing, setComputing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showLopModal, setShowLopModal] = useState(false);
  const [selectedEmpForLop, setSelectedEmpForLop] = useState(null);
  const [overrideLopDays, setOverrideLopDays] = useState(0);
  const [lopRemarks, setLopRemarks] = useState('');
  const [showReconcileModal, setShowReconcileModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        const p = new URLSearchParams(window.location.search);
        const urlComp = p.get('company_id');
        if (urlComp && cs.some(c => c.id === urlComp)) {
          setCompanyId(urlComp);
        } else if (cs.length === 1) {
          setCompanyId(cs[0].id);
        }
      } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const [dRes, lRes] = await Promise.all([
          axios.get(`${API_URL}/masters/departments?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/locations?company_id=${companyId}`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDepartments(dRes.data || []);
        setLocations(lRes.data || []);
      } catch { /* */ }
    })();
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId) {
      toast.error('Please select a company');
      return;
    }
    setLoading(true);
    try {
      // Always pass cycle — never pass year alone, the backend derives year from cycle
      const q = new URLSearchParams({ company_id: companyId, cycle });
      if (selDept) q.set('department_id', selDept);
      if (selLoc) q.set('location_id', selLoc);
      if (selLeaveType) q.set('leave_type', selLeaveType);

      const { data } = await axios.get(`${API_URL}/leave-policy/balances?${q.toString()}`, { withCredentials: true });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load balances');
    } finally {
      setLoading(false);
    }
  }, [companyId, cycle, selDept, selLoc, selLeaveType]);

  // Reload the entire grid whenever company OR cycle changes
  useEffect(() => {
    if (companyId) load();
  }, [companyId, cycle, load]);

  const filteredRows = rows.filter((r) => {
    if (!searchEmp.trim()) return true;
    const term = searchEmp.toLowerCase();
    return (
      (r.employee_name || '').toLowerCase().includes(term) ||
      (r.employee_code || '').toLowerCase().includes(term) ||
      (r.department || '').toLowerCase().includes(term)
    );
  });

  const exportCSV = () => {
    if (!filteredRows.length) return;
    const headers = [
      'Employee Code', 'Employee Name', 'Department', 'Location', 'Payroll Group',
      'Leave Type', 'Opening Balance', 'Earned', 'Availed', 'Adjusted',
      'Pending Approval', 'LOP Converted', 'Closing Balance', 'Period', 'Source', 'Last Updated By'
    ];
    const csvLines = [
      headers.join(','),
      ...filteredRows.map((r) => [
        `"${r.employee_code || ''}"`,
        `"${r.employee_name || ''}"`,
        `"${r.department || ''}"`,
        `"${r.location || ''}"`,
        `"${r.payroll_group || ''}"`,
        `"${r.leave_type || ''}"`,
        r.opening_balance ?? 0,
        r.earned ?? 0,
        r.availed ?? 0,
        r.adjusted ?? 0,
        r.pending_approval ?? 0,
        r.lop_converted ?? 0,
        r.closing_balance ?? 0,
        `"${r.period || ''}"`,
        `"${r.source || ''}"`,
        `"${r.last_updated_by || ''}"`
      ].join(','))
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave_balances_${companyId}_${cycle}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComputeCycleSummary = async () => {
    if (!companyId) return toast.error('Select a company first');
    setComputing(true);
    try {
      const res = await axios.post(`${API_URL}/leave-policy/cycle-summary/compute?company_id=${companyId}&cycle=${cycle}`, {}, { withCredentials: true });
      toast.success(res.data.message || `Computed 21st-20th cycle summary for ${res.data.computed_count} employees!`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compute cycle summary');
    } finally {
      setComputing(false);
    }
  };

  const handleSaveLopOverride = async () => {
    if (!selectedEmpForLop) return;
    if (!lopRemarks.trim()) return toast.error('Mandatory remarks required for LOP override');
    try {
      await axios.post(`${API_URL}/leave-policy/lop/override`, {
        company_id: companyId,
        cycle,
        employee_id: selectedEmpForLop.employee_id || selectedEmpForLop.id,
        override_lop_days: Number(overrideLopDays),
        remarks: lopRemarks.trim()
      }, { withCredentials: true });
      toast.success('LOP days overridden successfully');
      setShowLopModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to override LOP days');
    }
  };

  const handleExportLedgerCSV = async () => {
    if (!companyId) return toast.error('Select a company first');
    setExporting(true);
    try {
      const q = new URLSearchParams({ company_id: companyId, cycle });
      if (selDept) q.set('department_id', selDept);
      if (selLeaveType) q.set('leave_type', selLeaveType);
      if (searchEmp.trim()) q.set('search', searchEmp.trim());
      const url = `${API_URL}/leave-policy/ledger/export?${q.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Export failed' }));
        throw new Error(err.detail || 'Export failed');
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `leave_full_audit_${companyId}_${cycle}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success('Full Audit Export downloaded successfully');
    } catch (e) {
      toast.error(e.message || 'Export generation failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="leave-balances-page">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-lg"><Wallet className="w-6 h-6" /></span>
            <h1 className="text-xl font-bold text-slate-900">21st–20th Cycle Leave &amp; LOP Engine</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            21st to 20th cycle window, CL/EL slab accruals based on prev cycle present days, comp-off netting, 20th cutoff regularization, and LOP mapping.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdminOrHR && (
            <>
              <button
                onClick={handleComputeCycleSummary}
                disabled={!companyId || computing}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
              >
                {computing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Auto-Compute Cycle
              </button>
              <button
                onClick={handleExportLedgerCSV}
                disabled={!companyId || exporting}
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white font-semibold px-3.5 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
                title="Download 5-sheet Excel audit workbook for selected company, cycle, and filters"
              >
                {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Full Audit Export
              </button>
              <button
                onClick={() => setShowProcessorEntry(true)}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold px-3.5 py-2 rounded-lg text-xs border border-indigo-200 disabled:opacity-50"
              >
                <Plus size={14} /> Processor Entry
              </button>
              <button
                onClick={() => setShowBulkUpload(true)}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
              >
                <Upload size={14} /> Opening Balances
              </button>
              <button
                onClick={() => setShowReconcileModal(true)}
                disabled={!companyId}
                className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-3.5 py-2 rounded-lg text-xs shadow-sm disabled:opacity-50"
                title="Rebuild Leave Balance (CL, EL, Comp Off) using authoritative openings and deduplicated ledger"
              >
                <RotateCcw size={14} /> Rebuild Leave Balance
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toolbar Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Company *</label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white font-medium"
          >
            <option value="">— Select Company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Cycle (21st-20th) *</label>
          <input
            type="month"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-mono"
          />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Department</label>
          <select
            value={selDept}
            onChange={(e) => setSelDept(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-slate-50"
          >
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Leave Type</label>
          <select
            value={selLeaveType}
            onChange={(e) => setSelLeaveType(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-slate-50"
          >
            <option value="">All Leave Types</option>
            {['CL', 'SL', 'EL', 'PL', 'ML', 'CompOff', 'LOP'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase mb-1">Search Employee</label>
          <input
            value={searchEmp}
            onChange={(e) => setSearchEmp(e.target.value)}
            placeholder="Code or name..."
            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
          />
        </div>

        <div className="flex items-end gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-3 py-1.5 text-xs flex items-center justify-center gap-1 shadow-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Load
          </button>
          {filteredRows.length > 0 && (
            <button
              onClick={exportCSV}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg px-3 py-1.5 text-xs border border-slate-200 flex items-center justify-center gap-1"
            >
              <Download size={14} /> CSV
            </button>
          )}
        </div>
      </div>

      {/* Main Grid */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading leave balances...</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-600">No leave balances found.</p>
            <p className="text-xs text-slate-400 mt-1">Select a company and click <b>Load</b> to query leave records.</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3">Emp Code</th>
                  <th className="px-3 py-3">Employee Name</th>
                  <th className="px-3 py-3">Department</th>
                  <th className="px-3 py-3">Leave Type</th>
                  <th className="px-3 py-3 text-right">Opening Bal.</th>
                  <th className="px-3 py-3 text-right">Earned</th>
                  <th className="px-3 py-3 text-right">Availed</th>
                  <th className="px-3 py-3 text-right text-amber-700 bg-amber-50/60" title="Reserved until approval/rejection">Pending / Reserved</th>
                  <th className="px-3 py-3 text-right text-indigo-700 bg-indigo-50/60">Comp Off</th>
                  <th className="px-3 py-3 text-right text-teal-700 bg-teal-50/60">Reg. Adj.</th>
                  <th className="px-3 py-3 text-right text-blue-700 bg-blue-50/60">EL Adj.</th>
                  <th className="px-3 py-3 text-right text-violet-700 bg-violet-50/60">CL Adj.</th>
                  <th className="px-3 py-3 text-right text-rose-700 bg-rose-50/60">LOP Converted</th>
                  <th className="px-3 py-3 text-right text-emerald-700 bg-emerald-50/80 font-bold">Closing Bal.</th>
                  <th className="px-3 py-3">Cycle</th>
                  <th className="px-3 py-3">Source</th>
                  <th className="px-3 py-3">Updated By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-3 py-2.5 font-mono font-bold text-indigo-700">{r.employee_code}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-900">{r.employee_name}</td>
                    <td className="px-3 py-2.5 text-slate-600">{r.department}</td>
                    <td className="px-3 py-2.5 font-bold text-slate-800">{r.leave_type}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">
                      <div>{fmtINR(r.opening_balance)}</div>
                      {(r.has_derived_opening || (r.system_derived_historical_opening > 0)) && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800" title="System Derived Historical Opening">
                          System Derived ({fmtINR(r.system_derived_historical_opening)})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtINR(r.earned)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmtINR(r.availed)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-amber-700 bg-amber-50/30">{fmtINR(r.pending_approval || r.pending_reserved_leave)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-indigo-700 bg-indigo-50/30">{fmtINR(r.approved_compoff)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-teal-700 bg-teal-50/30">{fmtINR(r.regularized_days)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-blue-700 bg-blue-50/30">{r.leave_type === 'EL' ? fmtINR(r.adjusted) : '0.00'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-violet-700 bg-violet-50/30">{r.leave_type === 'CL' ? fmtINR(r.adjusted) : '0.00'}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-700 bg-rose-50/30">{fmtINR(r.lop_converted)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-700 bg-emerald-50/40 text-sm">
                      <div>{fmtINR(r.closing_available_balance ?? r.closing_balance)}</div>
                      {(Number(r.excess_utilization || r.historical_excess_leave || 0) > 0) && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-100 text-rose-800 border border-rose-200" title="Utilization exceeded total entitlement. Quarantined for HR Review; closing balance reflects verified available balance.">
                          Excess: {fmtINR(r.excess_utilization || r.historical_excess_leave)} (HR Review)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-slate-500 whitespace-nowrap">
                      <div>{r.period}</div>
                      {r.last_rebuild_date && (
                        <div className="text-[9px] text-slate-400">Rebuilt: {r.last_rebuild_date.slice(0, 10)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[11px]">
                      <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">{r.balance_source || r.source}</span>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-slate-500">{r.last_updated_by}</td>
                    {isAdminOrHR && (
                      <td className="px-4 py-2.5 text-[11px] text-right">
                        <button
                          onClick={() => { setSelectedEmpForLop(r); setOverrideLopDays(r.lop_converted || 0); setShowLopModal(true); }}
                          className="bg-amber-100 hover:bg-amber-200 text-amber-800 px-2.5 py-1 rounded font-bold"
                          title="Override LOP Days before payroll lock"
                        >
                          Override LOP
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin/HR Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal companyId={companyId} onClose={() => setShowBulkUpload(false)} onSuccess={() => { setShowBulkUpload(false); load(); }} />
      )}

      {/* HR Processor Entry Modal */}
      {showProcessorEntry && (
        <ProcessorEntryModal companyId={companyId} onClose={() => setShowProcessorEntry(false)} onSuccess={() => { setShowProcessorEntry(false); load(); }} />
      )}

      {/* LOP Manual Override Modal */}
      {showLopModal && selectedEmpForLop && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-amber-50 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-amber-900">Manual LOP Override</h3>
                <p className="text-xs text-amber-700">Pre-payroll lock manual override with mandatory audit remarks</p>
              </div>
              <button onClick={() => setShowLopModal(false)} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-xs font-semibold text-slate-800">{selectedEmpForLop.employee_name} ({selectedEmpForLop.employee_code})</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Cycle: <strong>{cycle}</strong> (21st M-1 to 20th M)</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Override LOP Days *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={overrideLopDays}
                  onChange={(e) => setOverrideLopDays(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono font-bold text-rose-700"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Mandatory Override Remarks *</label>
                <textarea
                  rows={3}
                  value={lopRemarks}
                  onChange={(e) => setLopRemarks(e.target.value)}
                  placeholder="Reason for manual LOP override (required)..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setShowLopModal(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
              <button
                onClick={handleSaveLopOverride}
                className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm"
              >
                Save LOP Override
              </button>
            </div>
          </div>
        </div>
      )}

      {showReconcileModal && (
        <ReconcileAuditModal
          companyId={companyId}
          cycle={cycle}
          departments={departments}
          onClose={() => setShowReconcileModal(false)}
          onSuccess={load}
        />
      )}
    </div>
  );
}

function BulkUploadModal({ companyId, onClose, onSuccess }) {
  const [uploadMode, setUploadMode] = useState('current_month'); // current_month | period_wise | consolidated
  const [csvText, setCsvText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const TEMPLATES = {
    current_month: {
      filename: 'leave_balance_current_month_template.csv',
      buttonText: 'Download Current Month Template',
      headers: 'Employee Code,Employee Name,Leave Type,Payroll Cycle,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted (+/-),LOP Days,Closing Balance,Remarks',
      sample: `EMP001,John Doe,CL,2026-07,5,1,0,0,0,6,July Cycle Accrual
EMP002,Jane Smith,EL,2026-07,10,1,0,0,0,11,July Cycle Accrual`
    },
    period_wise: {
      filename: 'leave_balance_period_wise_template.csv',
      buttonText: 'Download Period-wise Template',
      headers: 'Employee Code,Employee Name,Leave Type,Period From,Period To,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted (+/-),Comp-Off Availed,LOP Days,Closing Balance,Remarks',
      sample: `EMP001,John Doe,EL,2026-01-01,2026-06-30,10,6,2,0,1,0,13,H1 Period Balance
EMP002,Jane Smith,CL,2026-01-01,2026-03-31,6,3,1,0,0,0,8,Q1 Period Balance`
    },
    consolidated: {
      filename: 'leave_balance_consolidated_template.csv',
      buttonText: 'Download Consolidated Template',
      headers: 'Employee Code,Employee Name,Department,Location,Payroll Group,Employee Category,Leave Type,Financial Year,Period,Opening Balance,Leave Earned / Accrued,Leave Availed,Leave Adjusted,Comp-Off Availed,LOP Converted,Closing Balance,Last Updated Date,Upload Batch No.,Remarks',
      sample: `EMP001,John Doe,Engineering,HQ-Main,Staff Monthly,Regular,CL,2026-2027,2026-07,5,1,0,0,0,0,6,2026-07-20,MIGRATION-01,Migration Import
EMP002,Jane Smith,HR,Branch-South,Executive,EL,2026-2027,2026-07,12,1,2,0,0,0,11,2026-07-20,MIGRATION-01,Migration Import`
    }
  };

  const currentTemplate = TEMPLATES[uploadMode];

  const downloadSample = () => {
    const csvContent = `${currentTemplate.headers}\n${currentTemplate.sample}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = currentTemplate.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCsvText(evt.target.result);
    };
    reader.readAsText(file);
  };

  const processUpload = async () => {
    if (!csvText.trim()) return toast.error('Please upload or paste CSV rows');
    setUploading(true);
    setResult(null);

    const lines = csvText.trim().split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      setUploading(false);
      return toast.error('CSV must contain a header row and at least one data row');
    }

    const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = cols[idx] || '';
      });

      if (uploadMode === 'current_month') {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || obj['employee id'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          payroll_cycle: obj['payroll cycle'] || obj['payroll_cycle'] || obj['cycle'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted (+/-)'] || obj['leave adjusted'] || obj['adjusted'] || 0),
          lop_converted: Number(obj['lop days'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          remarks: obj['remarks'] || '',
        });
      } else if (uploadMode === 'period_wise') {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          period_from: obj['period from'] || obj['period_from'] || '',
          period_to: obj['period to'] || obj['period_to'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted (+/-)'] || obj['leave adjusted'] || obj['adjusted'] || 0),
          compoff_availed: Number(obj['comp-off availed'] || obj['compoff_availed'] || 0),
          lop_converted: Number(obj['lop days'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          remarks: obj['remarks'] || '',
        });
      } else {
        rows.push({
          employee_code: obj['employee code'] || obj['employee_code'] || '',
          employee_name: obj['employee name'] || obj['employee_name'] || '',
          department: obj['department'] || '',
          location: obj['location'] || '',
          payroll_group: obj['payroll group'] || obj['payroll_group'] || '',
          employee_category: obj['employee category'] || obj['employee_category'] || '',
          leave_type: (obj['leave type'] || obj['leave_type'] || 'CL').toUpperCase(),
          financial_year: obj['financial year'] || obj['financial_year'] || '',
          period: obj['period'] || '',
          opening_balance: Number(obj['opening balance'] || obj['opening_balance'] || 0),
          earned: Number(obj['leave earned / accrued'] || obj['leave earned'] || obj['earned'] || 0),
          availed: Number(obj['leave availed'] || obj['availed'] || 0),
          adjusted: Number(obj['leave adjusted'] || obj['adjusted'] || 0),
          compoff_availed: Number(obj['comp-off availed'] || obj['compoff_availed'] || 0),
          lop_converted: Number(obj['lop converted'] || obj['lop_converted'] || 0),
          closing_balance: Number(obj['closing balance'] || obj['closing_balance'] || 0),
          last_updated_date: obj['last updated date'] || obj['last_updated_date'] || '',
          upload_batch_no: obj['upload batch no.'] || obj['upload_batch_no'] || '',
          remarks: obj['remarks'] || '',
        });
      }
    }

    try {
      const { data } = await axios.post(
        `${API_URL}/leave-policy/balances/bulk-upload`,
        { company_id: companyId, upload_mode: uploadMode, rows },
        { withCredentials: true }
      );
      setResult(data);
      if (data.successful_rows > 0) {
        toast.success(`Successfully imported ${data.successful_rows} leave balance records via ${uploadMode} engine!`);
      }
      if (data.failed_rows > 0) {
        toast.warning(`${data.failed_rows} rows contained errors. Download the error report below.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload processing failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadErrorReport = () => {
    if (!result?.error_report_csv) return;
    const blob = new Blob([result.error_report_csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `upload_error_report_${result.batch_no}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">Admin / HR Leave Balance Bulk Upload</h3>
            <p className="text-xs text-slate-500">Dedicated templates &amp; specialized validation engines per upload type</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1 text-sm">
          {/* Mode Selector */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: 'current_month', label: '1. Current Month Upload' },
              { id: 'period_wise', label: '2. Period-wise Upload' },
              { id: 'consolidated', label: '3. Consolidated Upload' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { setUploadMode(m.id); setCsvText(''); setResult(null); }}
                className={`py-2.5 px-3 rounded-lg text-xs font-bold border text-center transition-all ${uploadMode === m.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between bg-slate-50 p-3.5 rounded-xl border border-slate-200">
            <div>
              <p className="text-xs font-bold text-slate-800">
                {uploadMode === 'current_month' && 'Current Month Template (Single Payroll Cycle)'}
                {uploadMode === 'period_wise' && 'Period-wise Template (Historical / Multi-Period Range)'}
                {uploadMode === 'consolidated' && 'Consolidated Migration Template (Full Master Import)'}
              </p>
              <p className="text-[11px] text-slate-500">Download the dedicated CSV template tailored for this upload type.</p>
            </div>
            <button
              onClick={downloadSample}
              className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-3.5 py-2 rounded-lg text-xs font-bold shadow-sm"
            >
              <Download size={14} /> {currentTemplate.buttonText}
            </button>
          </div>

          {/* Upload Input */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Upload {uploadMode === 'current_month' ? 'Current Month' : uploadMode === 'period_wise' ? 'Period-wise' : 'Consolidated'} CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="w-full border border-slate-300 rounded-lg p-2 text-xs bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">CSV Content Preview / Raw Editor</label>
            <textarea
              rows={6}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              className="w-full border border-slate-300 rounded-lg p-3 text-xs font-mono"
              placeholder={`Paste ${uploadMode} CSV rows here...`}
            />
          </div>

          {/* Results Block */}
          {result && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between font-bold text-xs">
                <span>Engine: <code className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-200 uppercase">{result.upload_mode}</code></span>
                <span>Batch No: <code className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-800">{result.batch_no}</code></span>
                <span className="text-emerald-700">Successful: {result.successful_rows}</span>
                <span className="text-rose-700">Failed: {result.failed_rows}</span>
              </div>
              {result.failed_rows > 0 && (
                <div className="pt-2 flex justify-end">
                  <button
                    onClick={downloadErrorReport}
                    className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold shadow-sm"
                  >
                    <Download size={14} /> Download {uploadMode} Error Report CSV
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
          <button
            disabled={uploading}
            onClick={processUpload}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50 flex items-center gap-1.5"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Validate &amp; Import ({uploadMode})
          </button>
        </div>
      </div>
    </div>
  );
}

function ProcessorEntryModal({ companyId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    employee_id: '', leave_type: 'CL', period_from: new Date().toISOString().slice(0, 7) + '-01', period_to: new Date().toISOString().slice(0, 10),
    entry_type: 'adjusted', amount: 1, reason: '',
  });
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/employees?company_id=${companyId}&limit=1000`, { withCredentials: true });
        setEmployees(data || []);
      } catch { /* */ }
    })();
  }, [companyId]);

  // Filter employees based on search term
  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const empCode = (emp.employment_details?.employee_code || '').toLowerCase();
    const firstName = (emp.personal_details?.first_name || '').toLowerCase();
    const lastName = (emp.personal_details?.last_name || '').toLowerCase();
    const fullName = (emp.personal_details?.full_name || `${firstName} ${lastName}`.trim()).toLowerCase();
    const department = (emp.employment_details?.department || '').toLowerCase();
    const mobile = (emp.personal_details?.mobile || '').toLowerCase();
    return empCode.includes(term) || fullName.includes(term) || department.includes(term) || mobile.includes(term);
  });

  // Helper to get employee display name and details
  const getEmployeeDisplayText = (emp) => {
    const empCode = emp.employment_details?.employee_code || '';
    const pd = emp.personal_details || {};
    const fullName = pd.full_name || `${pd.first_name || ''} ${pd.last_name || ''}`.trim() || empCode;
    const ed = emp.employment_details || {};
    const dept = ed.department || ed.department_name || '';
    const desig = ed.designation || ed.designation_name || '';
    return `${empCode} · ${fullName}${dept ? ` · ${dept}` : ''}${desig ? ` · ${desig}` : ''}`;
  };

  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    const selectedEmp = employees.find(e => e.id === empId);
    if (selectedEmp) {
      const pd = selectedEmp.personal_details || {};
      const ed = selectedEmp.employment_details || {};
      setForm(prev => ({
        ...prev,
        employee_id: empId,
        // Store extra details for display (optional but helpful)
        selected_employee_code: ed.employee_code || '',
        selected_employee_name: pd.full_name || `${pd.first_name || ''} ${pd.last_name || ''}`.trim(),
        selected_department: ed.department || ed.department_name || '',
        selected_designation: ed.designation || ed.designation_name || '',
        selected_location: ed.location || ed.location_name || '',
      }));
    } else {
      setForm(prev => ({
        ...prev,
        employee_id: '',
        selected_employee_code: '',
        selected_employee_name: '',
        selected_department: '',
        selected_designation: '',
        selected_location: '',
      }));
    }
  };

  const submit = async () => {
    if (!form.employee_id) return toast.error('Select an employee');
    if (!form.reason.trim()) return toast.error('Mandatory reason required for HR Processor Entry');

    setSaving(true);
    try {
      await axios.post(`${API_URL}/leave-policy/balances/processor-entry`, {
        company_id: companyId, ...form, amount: Number(form.amount)
      }, { withCredentials: true });
      toast.success('HR Processor entry posted successfully');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Processor entry failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">HR Direct Processor Entry</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Employee *</label>
            <input
              type="text"
              placeholder="Search employees by code, name, department, mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs mb-2"
            />
            <select
              value={form.employee_id}
              onChange={handleEmployeeSelect}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-medium"
            >
              <option value="">— Select Employee —</option>
              {filteredEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>{getEmployeeDisplayText(emp)}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Leave Type *</label>
              <select
                value={form.leave_type}
                onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-bold text-indigo-700"
              >
                {['CL', 'SL', 'EL', 'PL', 'ML', 'CompOff', 'LOP'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Adjustment Type *</label>
              <select
                value={form.entry_type}
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs capitalize font-semibold"
              >
                <option value="adjusted">Adjusted (+/-)</option>
                <option value="earned">Earned / Accrued (+)</option>
                <option value="availed">Availed (-)</option>
                <option value="lop">LOP Converted (-)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Period From</label>
              <input type="date" value={form.period_from} onChange={(e) => setForm({ ...form, period_from: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Period To</label>
              <input type="date" value={form.period_to} onChange={(e) => setForm({ ...form, period_to: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Amount (Days) *</label>
            <input
              type="number"
              step="0.5"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Mandatory Reason / Remarks *</label>
            <textarea
              rows={2}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs"
              placeholder="e.g. Approved HR manual adjustment for joining credit"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">Cancel</button>
          <button
            disabled={saving}
            onClick={submit}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50"
          >
            {saving ? 'Posting…' : 'Post Processor Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReconcileAuditModal({ companyId, cycle, departments = [], onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [auditData, setAuditData] = useState(null);

  // Filters
  const [selectedCycle, setSelectedCycle] = useState(cycle || new Date().toISOString().slice(0, 7));
  const [selectedDept, setSelectedDept] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [selectedLeaveType, setSelectedLeaveType] = useState('');
  const [isHistorical, setIsHistorical] = useState(true);
  const [reason, setReason] = useState('Historical Balance Rebuild / Reconciliation');

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        company_id: companyId,
        cycle: selectedCycle,
        dry_run: true,
        is_historical_rebuild: isHistorical,
      };
      if (selectedDept) payload.department = selectedDept;
      if (employeeCode.trim()) payload.employee_code = employeeCode.trim();
      if (selectedLeaveType) payload.leave_type = selectedLeaveType;

      const res = await axios.post(`${API_URL}/leave-policy/balances/rebuild`, payload, { withCredentials: true });
      setAuditData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rebuild preview');
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedCycle, selectedDept, employeeCode, selectedLeaveType, isHistorical]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const payload = {
        company_id: companyId,
        cycle: selectedCycle,
        dry_run: false,
        reason: reason.trim() || 'Historical Balance Rebuild / Reconciliation',
        is_historical_rebuild: isHistorical,
      };
      if (selectedDept) payload.department = selectedDept;
      if (employeeCode.trim()) payload.employee_code = employeeCode.trim();
      if (selectedLeaveType) payload.leave_type = selectedLeaveType;

      const res = await axios.post(`${API_URL}/leave-policy/balances/rebuild`, payload, { withCredentials: true });
      toast.success(`Successfully rebuilt ${res.data.updated_records || 0} leave balance record(s)!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to commit leave balance rebuild');
    } finally {
      setCommitting(false);
    }
  };

  const [expandedRow, setExpandedRow] = useState(null);
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'variances' | 'warnings' | 'ineligible' | 'derived' | 'excess'

  const report = auditData?.report || [];
  const derivedCount = report.filter(r => r.has_derived_opening).length;
  const excessCount = report.filter(r => r.historical_excess_leave > 0).length;
  const warningCount = report.filter(r => r.missing_source_warning).length;
  const ineligibleCount = report.filter(r => r.policy_eligibility && r.policy_eligibility.toLowerCase().includes('not eligible')).length;
  const varianceCount = auditData?.discrepancies_found || report.filter(r => r.status === 'needs_correction' || Math.abs(r.variance || 0) > 0.001).length;

  const filteredReport = report.filter(r => {
    if (filterTab === 'variances') return r.status === 'needs_correction' || Math.abs(r.variance || 0) > 0.001;
    if (filterTab === 'warnings') return Boolean(r.missing_source_warning);
    if (filterTab === 'ineligible') return r.policy_eligibility && r.policy_eligibility.toLowerCase().includes('not eligible');
    if (filterTab === 'derived') return Boolean(r.has_derived_opening);
    if (filterTab === 'excess') return (r.historical_excess_leave || 0) > 0;
    return true;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[97vw] w-full max-h-[94vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Rebuild Leave Balance (CL, EL &amp; Comp Off)
            </h3>
            <p className="text-xs text-slate-500">
              Authoritative balance rebuild engine. Validates uploaded opening balances, maps approved leave &amp; comp-off transactions, prevents unauthorized EL consumption, and derives LOP strictly from attendance absence days.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm font-bold">✕</button>
        </div>

        {/* Filter Toolbar */}
        <div className="p-4 bg-slate-50/70 border-b border-slate-200 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Effective Period</label>
            <input
              type="month"
              value={selectedCycle}
              onChange={(e) => setSelectedCycle(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 font-mono text-xs bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Department</label>
            <select
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs bg-white"
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name || d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Employee Code</label>
            <input
              placeholder="e.g. EMP001 (optional)"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">Leave Type</label>
            <select
              value={selectedLeaveType}
              onChange={(e) => setSelectedLeaveType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs bg-white font-semibold text-indigo-700"
            >
              <option value="">All Types (CL, EL, CompOff)</option>
              {['CL', 'EL', 'CompOff', 'SL', 'PL', 'ML'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchPreview}
              disabled={loading}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-bold py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Preview Rebuild
            </button>
          </div>
        </div>

        {/* Mode & Stats Header */}
        <div className="p-3 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-3">
            <span className="font-bold text-slate-700">Processing Mode:</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="rebuildMode"
                checked={isHistorical}
                onChange={() => setIsHistorical(true)}
                className="text-amber-600 focus:ring-amber-500"
              />
              <span className="font-semibold text-amber-900 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                1. Historical Balance Rebuild / Reconciliation
              </span>
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="rebuildMode"
                checked={!isHistorical}
                onChange={() => setIsHistorical(false)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                2. Normal Regular Processing Mode
              </span>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setFilterTab('all')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-mono transition-colors ${filterTab === 'all' ? 'bg-slate-800 text-white font-bold' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              All: <strong>{report.length}</strong>
            </button>
            <button
              onClick={() => setFilterTab('variances')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-mono transition-colors ${filterTab === 'variances' ? 'bg-blue-600 text-white font-bold' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
            >
              Variances: <strong>{varianceCount}</strong>
            </button>
            {warningCount > 0 && (
              <button
                onClick={() => setFilterTab('warnings')}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold flex items-center gap-1 transition-colors ${filterTab === 'warnings' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
              >
                <AlertTriangle size={12} /> Missing Sources: <strong>{warningCount}</strong>
              </button>
            )}
            {ineligibleCount > 0 && (
              <button
                onClick={() => setFilterTab('ineligible')}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold transition-colors ${filterTab === 'ineligible' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
              >
                Not Eligible: <strong>{ineligibleCount}</strong>
              </button>
            )}
            {derivedCount > 0 && (
              <button
                onClick={() => setFilterTab('derived')}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold transition-colors ${filterTab === 'derived' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}
              >
                Derived Comp Off: <strong>{derivedCount}</strong>
              </button>
            )}
            {excessCount > 0 && (
              <button
                onClick={() => setFilterTab('excess')}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-mono font-bold flex items-center gap-1 transition-colors ${filterTab === 'excess' ? 'bg-rose-600 text-white' : 'bg-rose-100 text-rose-800 hover:bg-rose-200'}`}
              >
                <AlertTriangle size={12} /> Excess Review: <strong>{excessCount}</strong>
              </button>
            )}
          </div>
        </div>

        {/* Diagnostic Preview Table */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {loading ? (
            <div className="py-16 text-center text-slate-500 flex flex-col items-center justify-center gap-2">
              <Loader2 className="w-7 h-7 animate-spin text-amber-600" />
              <span className="font-semibold text-sm">Calculating authoritative rebuild preview without modifying transactions...</span>
            </div>
          ) : filteredReport.length > 0 ? (
            <div className="border border-slate-200 rounded-xl overflow-x-auto max-h-[55vh] overflow-y-auto text-xs">
              <table className="w-full text-left">
                <thead className="bg-slate-100/90 sticky top-0 border-b border-slate-200 text-slate-700 font-bold uppercase text-[10px]">
                  <tr>
                    <th className="py-2.5 px-2 text-center w-8">#</th>
                    <th className="py-2.5 px-3">Emp Code &amp; Name</th>
                    <th className="py-2.5 px-2">Type</th>
                    <th className="py-2.5 px-2 text-center">Policy Eligibility</th>
                    <th className="py-2.5 px-3 text-right">Opening (Source &amp; Val)</th>
                    <th className="py-2.5 px-3 text-right">Earned</th>
                    <th className="py-2.5 px-3 text-right">Availed &amp; Comp Off</th>
                    <th className="py-2.5 px-2 text-right">Adj</th>
                    <th className="py-2.5 px-3 text-center">Absence / LOP</th>
                    <th className="py-2.5 px-2 text-right text-slate-500">Old Close</th>
                    <th className="py-2.5 px-2 text-right font-bold text-blue-700">Recalc Close</th>
                    <th className="py-2.5 px-2 text-right font-bold">Diff</th>
                    <th className="py-2.5 px-3 text-center">Diagnostic Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredReport.map((r, i) => (
                    <React.Fragment key={i}>
                      <tr
                        onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                        className={`cursor-pointer transition-colors ${
                          r.missing_source_warning ? 'bg-amber-50/50 hover:bg-amber-50/80' :
                          r.historical_excess_leave > 0 ? 'bg-rose-50/40 hover:bg-rose-50/70' :
                          r.status === 'needs_correction' ? 'bg-blue-50/30 hover:bg-blue-50/60' : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="py-2 px-2 text-center text-slate-400">
                          {expandedRow === i ? <ChevronDown size={14} className="text-slate-700 inline" /> : <ChevronRight size={14} className="inline" />}
                        </td>
                        <td className="py-2 px-3 font-medium text-slate-900">
                          <div className="font-mono font-bold text-indigo-700">{r.employee_code}</div>
                          <div className="text-[11px] text-slate-600 truncate max-w-[140px]">{r.employee_name}</div>
                          <div className="text-[10px] text-slate-400">{r.department}</div>
                        </td>
                        <td className="py-2 px-2 font-bold text-slate-800">
                          <span className="px-2 py-0.5 rounded bg-slate-100 font-mono text-[11px] border border-slate-200">
                            {r.leave_type}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center">
                          {r.policy_eligibility && r.policy_eligibility.toLowerCase().includes('not eligible') ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 inline-block" title={r.policy_eligibility}>
                              Not Eligible
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 inline-block">
                              Eligible
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          <div className="font-bold text-slate-800">{fmtINR(r.corrected_opening)}</div>
                          <div className="text-[10px] text-slate-500 font-sans truncate max-w-[130px] ml-auto" title={r.opening_source}>
                            {r.has_derived_opening ? (
                              <span className="text-amber-800 font-semibold bg-amber-50 px-1 py-0.2 rounded border border-amber-200 text-[9px]">
                                Derived ({fmtINR(r.system_derived_historical_opening)})
                              </span>
                            ) : (
                              r.opening_source || 'Default / Ledger'
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-slate-700">
                          <div>{fmtINR(r.earned)}</div>
                          <div className="text-[10px] text-slate-400 font-sans truncate max-w-[90px] ml-auto" title={r.earned_source}>{r.earned_source || '—'}</div>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-slate-700">
                          <div className="font-semibold">{fmtINR(r.availed)}</div>
                          <div className="text-[10px] text-indigo-600 font-sans truncate max-w-[130px] ml-auto" title={r.availed_source + (r.compoff_source ? ` | ${r.compoff_source}` : '')}>
                            {r.leave_type === 'CompOff' ? (r.compoff_source || r.availed_source || '—') : (r.availed_source || '—')}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-600">
                          <div>{fmtINR(r.adjustments)}</div>
                        </td>
                        <td className="py-2 px-3 text-center font-mono">
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="text-[11px] text-slate-600" title="Absence Days">Abs: <strong>{fmtINR(r.attendance_absence_days)}</strong></span>
                            <span className="text-slate-300">|</span>
                            <span className={`text-[11px] font-bold ${Number(r.lop_days || 0) > 0 ? 'text-rose-600' : 'text-slate-500'}`} title={r.lop_calculation}>
                              LOP: {fmtINR(r.lop_days)}
                            </span>
                          </div>
                          <div className="text-[9px] text-slate-400 truncate max-w-[140px] mx-auto font-sans" title={r.lop_calculation}>
                            {r.lop_calculation || 'No uncovered LOP'}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-400">{fmtINR(r.existing_closing)}</td>
                        <td className="py-2 px-2 text-right font-mono font-bold text-blue-700">{fmtINR(r.recalculated_closing)}</td>
                        <td className={`py-2 px-2 text-right font-mono font-bold ${r.variance > 0 ? 'text-emerald-600' : r.variance < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                          {r.variance > 0 ? `+${fmtINR(r.variance)}` : fmtINR(r.variance)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {r.missing_source_warning ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300" title={r.missing_source_warning}>
                              <AlertTriangle size={11} /> Missing Source
                            </span>
                          ) : r.historical_excess_leave > 0 ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                              Excess ({fmtINR(r.historical_excess_leave)})
                            </span>
                          ) : r.status === 'needs_correction' ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                              Rebuild Needed
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                              In Sync
                            </span>
                          )}
                        </td>
                      </tr>
                      {expandedRow === i && (
                        <tr className="bg-slate-50/90 border-b-2 border-indigo-200">
                          <td colSpan={13} className="p-3">
                            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                              <div className="space-y-1.5 p-2.5 bg-slate-50/70 rounded-lg border border-slate-100">
                                <div className="font-bold text-slate-700 uppercase text-[10px] tracking-wide flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-blue-600"></span> 1. Authoritative Opening
                                </div>
                                <div className="text-slate-700">Authoritative: <strong className="font-mono text-slate-900">{fmtINR(r.corrected_opening)}</strong></div>
                                <div className="text-slate-500 text-[11px]">Existing in DB: <span className="font-mono">{fmtINR(r.existing_opening)}</span></div>
                                <div className="text-[11px] text-slate-600">
                                  Source: <span className="font-mono font-medium text-indigo-700">{r.opening_source || 'Default / Ledger'}</span>
                                </div>
                                {r.missing_source_warning && (
                                  <div className="text-[11px] text-amber-800 bg-amber-50 p-2 rounded border border-amber-200 font-medium flex items-start gap-1.5 mt-1">
                                    <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-600" />
                                    <span>{r.missing_source_warning}</span>
                                  </div>
                                )}
                              </div>

                              <div className="space-y-1.5 p-2.5 bg-slate-50/70 rounded-lg border border-slate-100">
                                <div className="font-bold text-slate-700 uppercase text-[10px] tracking-wide flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span> 2. Credits &amp; Accruals
                                </div>
                                <div className="text-slate-700">Earned: <strong className="font-mono text-slate-900">{fmtINR(r.earned)}</strong></div>
                                <div className="text-[11px] text-slate-500">Earned Source: <span className="font-mono text-slate-700">{r.earned_source || 'None'}</span></div>
                                <div className="text-slate-700">Approved Adj: <strong className="font-mono text-slate-900">{fmtINR(r.adjustments)}</strong></div>
                                <div className="text-[11px] text-slate-500">Adj Source: <span className="font-mono text-slate-700">{r.adjustment_source || 'None'}</span></div>
                                {Number(r.compoff_value || 0) > 0 && (
                                  <div className="text-[11px] text-amber-800 bg-amber-50/70 p-1 rounded font-medium">
                                    Comp Off Record: {fmtINR(r.compoff_value)} ({r.compoff_source})
                                  </div>
                                )}
                              </div>

                              <div className="space-y-1.5 p-2.5 bg-slate-50/70 rounded-lg border border-slate-100">
                                <div className="font-bold text-slate-700 uppercase text-[10px] tracking-wide flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-rose-600"></span> 3. Debits &amp; LOP Breakdown
                                </div>
                                <div className="text-slate-700">Approved Availed: <strong className="font-mono text-slate-900">{fmtINR(r.availed)}</strong></div>
                                <div className="text-[11px] text-slate-500">Availed Source: <span className="font-mono text-slate-700">{r.availed_source || 'None'}</span></div>
                                <div className="text-slate-700">Attendance Absence: <strong className="font-mono text-slate-900">{fmtINR(r.attendance_absence_days)} days</strong></div>
                                <div className="text-slate-700">Uncovered LOP: <strong className="font-mono text-rose-700">{fmtINR(r.lop_days)} days</strong></div>
                                <div className="text-[10px] font-mono bg-white p-1.5 rounded border border-slate-200 text-slate-600">
                                  {r.lop_calculation || 'Absence fully covered by approved leaves'}
                                </div>
                              </div>

                              <div className="space-y-1.5 p-2.5 bg-slate-50/70 rounded-lg border border-slate-100">
                                <div className="font-bold text-slate-700 uppercase text-[10px] tracking-wide flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full bg-purple-600"></span> 4. Policy &amp; Rebuild Result
                                </div>
                                <div className="text-slate-700">
                                  Policy Eligibility: <strong className={r.policy_eligibility && r.policy_eligibility.toLowerCase().includes('not eligible') ? 'text-amber-700' : 'text-emerald-700'}>
                                    {r.policy_eligibility || 'Eligible'}
                                  </strong>
                                </div>
                                <div className="text-slate-700">
                                  Closing Available: <strong className="font-mono text-blue-700">{fmtINR(r.closing_available_balance ?? r.recalculated_closing)}</strong>
                                </div>
                                {(Number(r.excess_utilization || r.historical_excess_leave || 0) > 0) && (
                                  <div className="text-[11px] text-rose-800 bg-rose-50 p-2 rounded border border-rose-200 font-medium flex items-center gap-1.5">
                                    <AlertCircle size={13} className="shrink-0 text-rose-600" />
                                    <span>Excess Utilization: {fmtINR(r.excess_utilization || r.historical_excess_leave)} days (HR Review Required)</span>
                                  </div>
                                )}
                                <div className="text-[10px] text-slate-400 font-mono mt-1 pt-1 border-t border-slate-200">
                                  {r.breakdown_notes || 'Formula: Opening + Earned + Adjustments - Availed = Closing'}
                                </div>
                              </div>
                            </div>

                            {/* Section 5 & 4: Dedicated Comp Off Entitlement & Transaction Audit Ledger */}
                            {(r.leave_type === 'CompOff' || (r.compoff_audit_ledger && r.compoff_audit_ledger.length > 0)) && (
                              <div className="mt-3 bg-white p-3.5 rounded-xl border border-indigo-200 shadow-sm text-xs space-y-3">
                                <div className="flex items-center justify-between border-b border-indigo-100 pb-2">
                                  <div className="font-bold text-indigo-900 uppercase text-[11px] tracking-wide flex items-center gap-1.5">
                                    <CheckCircle2 size={14} className="text-indigo-600" />
                                    Section 5: Comp Off Entitlement &amp; Reconciled Balance Bridge
                                  </div>
                                  <div className="text-[10px] text-slate-500 font-mono">
                                    Authoritative Source: {r.source_of_historical_adjustments || r.opening_source || 'Verified September Snapshot'}
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-center">
                                  <div className="p-2 bg-blue-50 rounded border border-blue-200">
                                    <div className="text-[9px] uppercase font-bold text-blue-800">Opening Entitlement</div>
                                    <div className="text-sm font-bold font-mono text-blue-900 mt-0.5">{fmtINR(r.opening_entitlement ?? r.corrected_opening)}</div>
                                    <div className="text-[8px] text-blue-600">Sep Closing Carry-Fwd</div>
                                  </div>
                                  <div className="p-2 bg-emerald-50 rounded border border-emerald-200">
                                    <div className="text-[9px] uppercase font-bold text-emerald-800">New Entitlement</div>
                                    <div className="text-sm font-bold font-mono text-emerald-900 mt-0.5">{fmtINR(r.new_entitlement ?? r.earned)}</div>
                                    <div className="text-[8px] text-emerald-600">Earned in Cycle</div>
                                  </div>
                                  <div className="p-2 bg-purple-50 rounded border border-purple-200">
                                    <div className="text-[9px] uppercase font-bold text-purple-800">Approved Utilization</div>
                                    <div className="text-sm font-bold font-mono text-purple-900 mt-0.5">{fmtINR(r.approved_utilization ?? r.availed)}</div>
                                    <div className="text-[8px] text-purple-600">Post-Cutover Usage</div>
                                  </div>
                                  <div className="p-2 bg-slate-50 rounded border border-slate-200">
                                    <div className="text-[9px] uppercase font-bold text-slate-700">Expired Entitlement</div>
                                    <div className="text-sm font-bold font-mono text-slate-800 mt-0.5">{fmtINR(r.expired_entitlement || 0)}</div>
                                    <div className="text-[8px] text-slate-500">Unconsumed Only</div>
                                  </div>
                                  <div className="p-2 bg-teal-50 rounded border border-teal-200">
                                    <div className="text-[9px] uppercase font-bold text-teal-800">Closing Available</div>
                                    <div className="text-sm font-bold font-mono text-teal-900 mt-0.5">{fmtINR(r.closing_available_balance ?? r.recalculated_closing)}</div>
                                    <div className="text-[8px] text-teal-600">Verified Available</div>
                                  </div>
                                  <div className={`p-2 rounded border ${Number(r.excess_utilization || 0) > 0 ? 'bg-rose-50 border-rose-300' : 'bg-slate-50 border-slate-200'}`}>
                                    <div className={`text-[9px] uppercase font-bold ${Number(r.excess_utilization || 0) > 0 ? 'text-rose-800' : 'text-slate-700'}`}>Excess Utilization</div>
                                    <div className={`text-sm font-bold font-mono mt-0.5 ${Number(r.excess_utilization || 0) > 0 ? 'text-rose-900 font-extrabold' : 'text-slate-800'}`}>
                                      {fmtINR(r.excess_utilization || 0)}
                                    </div>
                                    <div className={`text-[8px] ${Number(r.excess_utilization || 0) > 0 ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
                                      {Number(r.excess_utilization || 0) > 0 ? 'HR Review Flagged' : 'None'}
                                    </div>
                                  </div>
                                  <div className="p-2 bg-amber-50 rounded border border-amber-200">
                                    <div className="text-[9px] uppercase font-bold text-amber-800">Pending Investigation</div>
                                    <div className="text-sm font-bold font-mono text-amber-900 mt-0.5">{fmtINR(r.pending_investigation_amount || 0)}</div>
                                    <div className="text-[8px] text-amber-600">Audit Status</div>
                                  </div>
                                </div>

                                {r.compoff_audit_ledger && r.compoff_audit_ledger.length > 0 && (
                                  <div className="mt-2">
                                    <div className="font-bold text-slate-700 uppercase text-[10px] tracking-wide mb-1 flex items-center justify-between">
                                      <span>Section 4: Transaction-Level Audit Ledger ({r.compoff_audit_ledger.length} items)</span>
                                      <span className="text-slate-400 font-mono text-[9px]">Rebuild Run ID: {r.compoff_audit_ledger[0]?.rebuild_run_id || '—'}</span>
                                    </div>
                                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                                      <table className="w-full text-[10px] divide-y divide-slate-200">
                                        <thead className="bg-slate-100 text-slate-700 font-semibold">
                                          <tr>
                                            <th className="py-1 px-2 text-left">Txn ID / Ref</th>
                                            <th className="py-1 px-1.5 text-left">Earned Date</th>
                                            <th className="py-1 px-1.5 text-left">Credited / Leave</th>
                                            <th className="py-1 px-1.5 text-left">Status</th>
                                            <th className="py-1 px-1.5 text-right">Days</th>
                                            <th className="py-1 px-1.5 text-center">In Sep Closing?</th>
                                            <th className="py-1 px-1.5 text-center">In Oct Calc?</th>
                                            <th className="py-1 px-2 text-left">Audit Disposition &amp; Investigation Finding</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                          {r.compoff_audit_ledger.map((txn, txIdx) => (
                                            <tr key={txIdx} className={txIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                              <td className="py-1 px-2 font-mono font-bold text-slate-800 truncate max-w-[120px]" title={txn.source_transaction_id}>
                                                {txn.source_transaction_id}
                                              </td>
                                              <td className="py-1 px-1.5 font-mono text-slate-600">{txn.comp_off_earned_date || '—'}</td>
                                              <td className="py-1 px-1.5 font-mono text-slate-600">{txn.comp_off_leave_date !== '—' ? txn.comp_off_leave_date : (txn.comp_off_credited_date || '—')}</td>
                                              <td className="py-1 px-1.5">
                                                <span className={`px-1 py-0.2 rounded text-[9px] font-semibold ${
                                                  txn.approval_status === 'verified_closing' ? 'bg-blue-100 text-blue-800' :
                                                  txn.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                                                  'bg-slate-100 text-slate-700'
                                                }`}>
                                                  {txn.approval_status}
                                                </span>
                                              </td>
                                              <td className="py-1 px-1.5 text-right font-mono font-bold text-slate-900">{fmtINR(txn.days)}</td>
                                              <td className="py-1 px-1.5 text-center">
                                                <span className={`px-1 py-0.2 rounded font-bold text-[9px] ${txn.is_included_in_september_closing ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-400'}`}>
                                                  {txn.is_included_in_september_closing ? 'YES' : 'NO'}
                                                </span>
                                              </td>
                                              <td className="py-1 px-1.5 text-center">
                                                <span className={`px-1 py-0.2 rounded font-bold text-[9px] ${txn.is_included_in_october_calculation ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-400'}`}>
                                                  {txn.is_included_in_october_calculation ? 'YES' : 'NO'}
                                                </span>
                                              </td>
                                              <td className="py-1 px-2 text-slate-700">
                                                <div className="font-medium text-indigo-900">{txn.audit_disposition}</div>
                                                {txn.investigation_category && txn.investigation_category !== 'Baseline Cutover Balance' && txn.investigation_category !== 'Valid Utilization' && txn.investigation_category !== 'Valid New Entitlement' && (
                                                  <div className="text-[9px] text-amber-700 font-semibold mt-0.5">
                                                    Category: {txn.investigation_category}
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-400">No records found matching filters.</div>
          )}
        </div>

        {/* Audit Reason & Confirm Rebuild Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col md:flex-row items-center justify-between gap-3 text-xs">
          <div className="flex-1 w-full md:w-auto">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Audit trail rebuild reason..."
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700">
              Cancel
            </button>
            <button
              onClick={handleCommit}
              disabled={loading || committing || !report.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-xs font-bold rounded-lg shadow-sm disabled:opacity-50 flex items-center gap-1.5"
            >
              {committing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Confirm Rebuild ({auditData?.discrepancies_found || 0} variances)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

